/**
 * utilities/index.tsx
 * desc: common utility functions and helper methods
 * note: public fns are exported while private fns are prefixed with `_`
 */
import { BrowserRouter, MemoryRouter, Routes, Route } from 'react-router';
import type { ComponentType } from 'react';
import { toastr } from 'react-redux-toastr';
import { translate } from './localization';
import constants from '../settings/constants';

// the toaster context shape getErrorMessage massages before interpolation;
// values come from the API error body, so everything else stays unknown
export interface ErrorMessageContext {
    components?: Array<Record<string, unknown>>;
    summary?: unknown;
    [key: string]: unknown;
}

// the error shape showErrorMessage consumes (structurally ApiRequestError)
interface ShownError {
    name?: string;
    id?: string | null;
    errorCode?: string | null;
    internalDescription?: string | null;
    context?: ErrorMessageContext | null;
}

/**
 * applyFilters
 * desc: TEMPORARY function to apply filters on data array
 * note: this function will be removed when filter API will be provided
 * @param  {Array}   data      initial data array
 * @param  {Object}  filters   filters that needs to be applied
 * @return {Array}             filtered result
 */
export function applyFilters<T extends Record<string, unknown>>(data: T[], filters: Record<string, unknown[]>): T[] {
    const filterKeys = Object.keys(filters);
    let result = data;
    if (filterKeys.length > 0) {
        filterKeys.forEach(filterKey => {
            const selected: Array<(item: T) => boolean> = [];
            selected.push((item) => {
                let result = false;
                const value = item[filterKey];
                if (Array.isArray(value)) {
                    result = value.length > 0 ? value.some(o => filters[filterKey].includes(o)) : false;
                } else {
                    result = filters[filterKey].includes(value);
                }
                return result;
            });
            result = result.filter(item => selected.some(f => f(item)));
        });
    }
    return result;
}


/**
 * applySearch
 * desc: TEMPORARY function to apply search on data array
 * note: this function will be removed when search API will be provided
 * @param  {Array}   data      initial data array
 * @param  {Object}  filters   filters that needs to be applied
 * @return {Array}             filtered result
 */
export function applySearch<T extends Record<string, unknown>>(data: T[], searchFields: string[], searchValue: string): T[] {
    const searchValueFormatted = searchValue?.toLowerCase().trim();
    const selected: Array<(item: T) => boolean | undefined> = [];
    let result = data;
    if (searchFields.length > 0) {
        searchFields.forEach(searchField => {
            selected.push((item) => {
                let result: boolean | undefined = false;
                const value = item[searchField];
                if (Array.isArray(value)) {
                    result = value.length > 0 ? value.some(o => o.toLowerCase().trim().includes(searchValueFormatted)) : false;
                } else {
                    // non-string values crash here — known quirk, kept deliberately
                    result = (value as string | undefined)?.toLowerCase().trim().includes(searchValueFormatted);
                }
                return result;
            });
        });

        result = data.filter(item => selected.some(f => f(item)));
    }
    return result;
}


/**
 * capitalizeEachWord
 * desc: returns new string w the first letter of each word capitalized
 * note: useful for normalizing system text such as appUrls, etc.
 * @param  {...any} args  Arguments
 * @return {string}       CSS classes (e.g. 'input error')
 */
export function capitalizeEachWord(str: string): string | undefined {
    if (typeof str === 'string') {
        return str.replace(/\w\S*/g, (txt) => {
            return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
        });
    }
}


/**
 * classNames
 * desc: useful for constructing a css class string
 * note: recursive function to account for nested arrays/objects
 * @param  {...any} args  Arguments
 * @return {string}       CSS classes (e.g. 'input error')
 */
export function classNames(...args: unknown[]): string {
    const classList: string[] = [];

    args.forEach(arg => {
        // first check if arg exists
        // then if string, add to the classlist
        if (arg && typeof arg === 'string') classList.push(arg);
        // if array, iterate and add to the classlist
        else if (Array.isArray(arg)) {
            arg.forEach(item => {
                if (Array.isArray(item) || typeof item === 'object') classList.push(classNames(item));
                else if (typeof item === 'string') classList.push(item);
            });
        }
        // if object, iterate and add to the classlist
        // note: all truthy values (including 'false'), are added to the classlist
        else if (typeof arg === 'object' && arg !== null) {
            const argObj = arg as Record<string, unknown>;
            const keys = Object.keys(argObj);
            keys.forEach(key => {
                if (Array.isArray(argObj[key]) || typeof argObj[key] === 'object') classList.push(classNames(argObj[key]));
                else if (argObj[key]) classList.push(key);
            });
        }
    });

    return classList.join(' ');
}


/**
 * convertParamsStrToObj
 * desc: converts query string params into object
 * note:
 * @param {string} qs  Query String
 * @returns            Formatted Object
 */
export function convertParamsStrToObj(str = ''): Record<string, string | undefined | Array<string | undefined>> {
    const obj: Record<string, string | undefined | Array<string | undefined>> = {};

    // check if argument exists and is valid
    if (str && typeof str === 'string') {
        const qs = str.split('?')[1] || '';
        const qsParams = qs.split('&');

        // iterate over qs parameters
        // note: split BEFORE decoding — a decoded value may itself contain '&' or '='
        // (convertParamsObjToStr percent-encodes them per component)
        qsParams.forEach(pair => {
            const kv = pair.split('=');
            const key = decodeURIComponent(kv[0]);
            const val = kv[1] === undefined ? undefined : decodeURIComponent(kv[1]);

            const existing = obj[key];
            if (existing) {
                if (Array.isArray(existing)) {
                    existing.push(val);
                } else {
                    obj[key] = [ existing, val ];
                }
            } else {
                obj[key] = val;
            }
        });
    }

    return obj;
}


/**
 * convertParamsObjToStr
 * desc: converts params object into query string
 * note:
 * @param {object} obj  Params object
 * @returns             Query String
 */
export function convertParamsObjToStr(obj: Record<string, unknown> = {}): string {
    const keys = obj ? Object.keys(obj) : [];
    const paramsArr: string[] = [];

    // per-component encoding: encodeURI over the joined string leaves reserved
    // characters ('#', '&', '=') raw, which breaks navigate() and corrupts parsing
    keys.forEach(key => {
        const value = obj[key];
        if (typeof value === 'string') {
            paramsArr.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        } else if (Array.isArray(value)) {
            value.forEach((item) => {
                paramsArr.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
            });
        }
    });

    // code-unit comparison (not localeCompare) keeps the param order deterministic
    // regardless of the user's locale
    paramsArr.sort((a, b) => {
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
    });

    return paramsArr.length > 0 ? `?${paramsArr.join('&')}` : '';
}


/**
 * decodeHtml
 * desc: decode html special chars
 * note: opposite to encodeHtml function
 * @param  {string} str   initial string
 * @return {string}       result html
 */
export function  decodeHtml (str: string): string {
    const tagsToReplace: Record<string, string> = {
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>'
    };

    return str.replace(/(&amp;|&lt;|&gt;)/g, (tag) => (tagsToReplace[tag] || tag));
}


/**
 * encodeHtml
 * desc: sanitizing string by replacing html special chars
 * note: opposite to decodeHtml function
 * @param  {string} html  initial html
 * @return {string}       result string
 */
export function encodeHtml (html: string): string {
    const tagsToReplace: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;'
    };

    return html.replace(/[&<>]/g, (tag) => (tagsToReplace[tag] || tag));
}




/**
 * downloadFileClient
 * desc: init file download
 * note:
 * @param  {string} fileData
 * @param  {string} fileNama
 * @return {undefined}
 */
export function downloadFileClient (fileData: unknown, format: string, fileName: string): void {
    let data = fileData;
    if (format === 'text/plain') {
        data = JSON.stringify(fileData);
    }
    const link = document.createElement('a');

    // non-string data outside the text/plain branch is coerced by encodeURIComponent
    link.setAttribute('href', `data:${format};charset=utf-8,` + encodeURIComponent(data as string));
    link.setAttribute('download', fileName);
    link.style.display = 'none';

    document.body.appendChild(link);

    link.click();

    link.remove();
}



/**
 * downloadFileClientWithResponse
 * desc: init file download
 * note:
 * @param  {string} fileData
 * @param  {string} fileNama
 * @return {undefined}
 */
export async function downloadFileClientWithResponse (response: Response, fileName: string): Promise<void> {
    // Convert the response to a Blob
    const blob = await response.blob();

    // Create a temporary anchor element
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = fileName; // Specify the file name for the download
    document.body.appendChild(a); // Append the anchor to the document
    a.click(); // Programmatically click the anchor to trigger the download
    a.remove(); // Remove the anchor after the download starts

    // Release the object URL to free up memory
    URL.revokeObjectURL(a.href);
}



/**
 * getErrorMessage
 * desc: returns a translated error message
 * note: if translation key does not exists the fallback (when provided) or the
 *       default message will be returned
 * @param  {string} key       Translation key
 * @param  {object} context   Interpolation values (optional)
 * @param  {string} fallback  Message to use when no translation exists (optional)
 * @return {string}           Translated error message
 */
export function getErrorMessage (key?: string | null, context?: ErrorMessageContext | null, fallback?: string | null): string {
    let message = translate('toaster.error.DEFAULT');

    if (context?.components && context.components.length > 0) {
        context = {
            ...context,
            ...context.components[0]
        } as ErrorMessageContext;
    }

    // PATH_HAS_INCOMPLETE_STEPS ships summary as [{ stepId, stepName, reason }] —
    // collapse it to a printable list of step names for interpolation; an empty
    // result is removed (react-intl treats a present-but-undefined value as
    // filled) so the unfilled-placeholder fallback below kicks in
    if (Array.isArray(context?.summary)) {
        const summary = context.summary.map(step => step?.stepName).filter(Boolean).join(', ');
        context = { ...context };
        if (summary) {
            context.summary = summary;
        } else {
            delete context.summary;
        }
    }

    if (key) {
        message = translate(`toaster.error.${key}`, context ?? undefined);
        // if no translation exists, fallback to a generic *_NOT_FOUND message
        // (matching the entity prefix), the provided fallback, or the default message
        if (message.includes('[TRANSLATE ERROR]')) {
            message = _getNotFoundFallbackMessage(key) || fallback || translate('toaster.error.DEFAULT');
        } else if (/\{\w+/.test(message)) {
            // a context-aware message arrived without its context values —
            // react-intl returns the raw pattern with unfilled placeholders
            message = translate('toaster.error.DEFAULT');
        }
    }

    return message;
}


/**
 * _getNotFoundFallbackMessage
 * desc: builds a generic message for *_NOT_FOUND error codes that have no
 *       dedicated translation key, deriving the entity name from the prefix
 * note: handles the *_REFERENCE_NOT_FOUND variant; returns null for any code
 *       that does not end in _NOT_FOUND so callers can fall back to DEFAULT
 * @param  {string} key  error code (e.g. OAUTH_TOKEN_SECRET_NOT_FOUND)
 * @return {string|null} translated message, or null when not a *_NOT_FOUND code
 */
function _getNotFoundFallbackMessage (key: string): string | null {
    const NOT_FOUND = '_NOT_FOUND';
    const REFERENCE_NOT_FOUND = '_REFERENCE_NOT_FOUND';

    if (!key.endsWith(NOT_FOUND)) {
        return null;
    }

    const isReference = key.endsWith(REFERENCE_NOT_FOUND);
    const suffix = isReference ? REFERENCE_NOT_FOUND : NOT_FOUND;
    const prefix = key.slice(0, -suffix.length);

    if (!prefix) {
        return null;
    }

    // turn TOKEN_SECRET into "Token Secret" while keeping short all-caps
    // tokens (API, SSO, ID, ...) as acronyms instead of down-casing them
    const entity = prefix
        .split('_')
        .filter(Boolean)
        .map(word => (word.length <= 3 ? word : word.charAt(0) + word.slice(1).toLowerCase()))
        .join(' ');

    const fallbackKey = isReference
        ? 'toaster.error.GENERIC_REFERENCE_NOT_FOUND'
        : 'toaster.error.GENERIC_NOT_FOUND';

    // if the generic key is missing from a locale, return null so the caller
    // falls back to DEFAULT rather than surfacing the raw [TRANSLATE ERROR]
    const message = translate(fallbackKey, { entity });
    return message.includes('[TRANSLATE ERROR]') ? null : message;
}


/**
 * getFieldValue
 * desc: returns a value of the input field by selector
 * note: useful for work with uncontrolled components
 * @param  {string} selector     Dom selector
 * @param  {object} parentNode   Dom node
 * @return {string}              Elements value
 */
export function getFieldValue (selector: string, parentNode?: ParentNode | null): string | boolean | null {
    let value: string | boolean | null = null;
    const el = (parentNode || document).querySelector(selector) as HTMLInputElement | null;
    if (el) {
        if (el.type === 'checkbox') {
            value = el.checked;
        } else {
            value = el.value.trim();
        }

    }

    return value;
}


/**
 * getIntentSelections
 * desc: returns a filtered list of selected intent objects
 * note: intents api returns objects, but labeled messages only has ids
 * @param  {array} intentsList  List of intent objects
 * @param  {array} selectedIds  List of selected intent ids
 * @return {array}              Intent selections
 */
export function getIntentSelections<T extends { id: unknown }>(intents: T[] = [], selectedIds: unknown[] = []): T[] {
    let intentSelections: T[] = [];

    if (intents.length && selectedIds.length) {
        intentSelections = intents.filter(intent => selectedIds.includes(intent.id));
    }

    return intentSelections;
}


/**
 * getIntlDateTime
 * desc: formats date/time based on locale designation
 * note: useful for formatting timestamp records
 * @param  {string} timestamp  ISO string (e.g. '2020-04-20T19:45:18Z')
 * @param  {string} locale     User locale string (e.g. 'fr-fr')
 * @param  {object} opts       Options (optional)
 *                             {
 *                                 weekday: 'narrow' | 'short' | 'long',
 *                                 era: 'narrow' | 'short' | 'long',
 *                                 year: 'numeric' | '2-digit',
 *                                 month: 'numeric' | '2-digit' | 'narrow' | 'short' | 'long',
 *                                 day: 'numeric' | '2-digit',
 *                                 hour: 'numeric' | '2-digit',
 *                                 minute: 'numeric' | '2-digit',
 *                                 second: 'numeric' | '2-digit',
 *                                 timeZoneName: 'short' | 'long',
 *                                 timeZone: 'Asia/Shanghai',
 *                                 hour12: true | false,
 *                                 hourCycle: 'h11' | 'h12' | 'h23' | 'h24',
 *                                 formatMatcher: 'basic' | 'best fit',
 *                             }
 * @return {string}            Formatted string (e.g. '01/01/2020 8:01 AM')
 */
export function getIntlDateTime(timestamp: string | number = 0, locale?: string, options?: Intl.DateTimeFormatOptions): string | undefined {
    if (timestamp) {
        const date = new Date(timestamp);
        const userLocale = locale || constants.LOCALES.DEFAULT;
        const opts = options || {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        };

        return new Intl.DateTimeFormat(userLocale, opts).format(date);
    }
}


/**
 * getIntlNumber
 * desc: formats large number based on locale designation
 * note: useful for currency and search results
 * @param  {number|string} num     Raw number or string (e.g. 1000000.01)
 * @param  {string}        locale  User locale string (e.g. 'fr-fr')
 * @param  {object}        options Options (optional)
 * @return {string}                Formatted string (e.g. '1 000 000,01')
 */
export function getIntlNumber(num: number | string = 0, locale: string = constants.LOCALES.DEFAULT, options: Intl.NumberFormatOptions = {}): string {
    return  new Intl.NumberFormat(locale, options).format(num as number);
}


/**
 * getObjectFromArray
 * desc: returns an object w the property value as the key
 * note: expects an array of objects and a unique property
 * @param  {array}  arr   Original Array
 * @param  {string} prop  Property to use as object key
 * @return {object}       Formatted object
 */
export function getObjectFromArray<T extends Record<string, unknown>>(arr: T[] = [], prop = ''): Record<string, T> {
    const obj: Record<string, T> = {};

    arr.forEach(item => {
        // object keys coerce, so any property value works here
        obj[item[prop] as string] = item;
    });

    return obj;
}


/**
 * getQueryStringObject
 * desc: converts query string into object
 * @return {Object}  query string object
 */
export function getQueryStringObject(): Record<string, string | undefined> {
    const qs = window?.location?.search?.split('?')[1];
    const qsArr = (qs) ? qs.split('&') : [];

    const qsObj: Record<string, string | undefined> = {};

    // if query params exist, loop
    if (qsArr.length) {
        qsArr.forEach(param => {
            const set = param.split('=');
            qsObj[set[0]] = set[1];
        });
    }

    return qsObj;
}


/**
 * getRandomInt
 * desc: returns a random integer
 * note: if min/max exist then integer falls within specified range
 * @param  {number} min  Mininum value (optional)
 * @param  {number} max  Maximum value (optional)
 * @return {number}      Random integer between mininum and maximum
 */
export function getRandomInt(min?: number, max?: number): number {
    const lower = Number(min) ? Math.ceil(min!) : 0;
    const upper = Number(max) ? Math.floor(max!) : Math.floor(_getRandomFraction() * 1000000);

    return Math.floor(_getRandomFraction() * (upper - lower) + lower);
}


/**
 * getCurrencySeparators
 * desc: returns thousands and decimal separators based on locale
 * note: useful for formatting numbers in user interface
 * @param  {object}  options
 * @param  {string}  options.locale    User locale (optional)
 * @param  {string}  options.currency  Currency code (optional, default is 'USD')
 * @return {object}                   Object with thousands and decimal separators
 *                                    e.g. { thousands: ',', decimal: '.' }
 */
export function getCurrencySeparators({ locale = constants.LOCALES.DEFAULT, currency = 'USD' }: { locale?: string; currency?: string } = {}): { thousands: string | undefined; decimal: string | undefined } {
    // formatToParts gives you each “token” of the formatted number
    try {
        const parts = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
        }).formatToParts(1234567.89);
        return {
            thousands: parts.find(p => p.type === 'group')?.value || undefined,
            decimal:   parts.find(p => p.type === 'decimal')?.value || undefined
        };
    } catch {
        return {
            thousands: undefined,
            decimal:   undefined
        };
    }
}



/**
 * prepareJsonPatchPath
 * desc: prepares a path string for use in JSON Patch operations
 * note: escapes '~' to '~0' and '/' to '~1' as per RFC 6901
 * @param  {string} path  Original path string
 * @return {string}       Escaped path string
 */
export function prepareJsonPatchPath(path: string): string {
    // Escape '~' to '~0' and '/' to '~1' as per RFC 6901
    return path.replaceAll('~', '~0').replaceAll('/', '~1');
}



/**
 * getStepOutputList
 * desc: array of step output data objects
 * @param  {object}  Step Output Schema
 * @return {array}   List of step outputs
 */
export interface StepOutput {
    key: string;
    id?: string;
    titleOverride?: string;
    title?: string;
    [key: string]: unknown;
}

export function getStepOutputList(outputObj: Record<string, Record<string, unknown>> = {}): StepOutput[] {
    const outputList: StepOutput[] = [];
    for (const property in outputObj) {
        outputList.push({ key: property, ...outputObj[property] } as StepOutput);
    }
    return outputList.toSorted((a, b) => {
        const aKey = a.titleOverride || a.title || a.key || '';
        const bKey = b.titleOverride || b.title || b.key || '';
        return aKey.localeCompare(bKey);
    });
}



/**
 * getTwoDigitStr
 * desc: returns string w minimum of two-digits (eg. 1 => '01')
 * note: useful for pre-populating input="date" fields
 * @param  {number|string} n  Number
 * @return {string}           Two-digit string
 */
export function getTwoDigitString(n: number | string): string | undefined {
    const num = Number(n);
    if (num) {
        if (num < 10) return `0${num}`;
        return `${num}`;
    }
}



// `${name}` placeholders of a template; a dotted name (`${a.b}`) contributes
// its head key, matching _getObjProp's nested lookup
type TemplateParamNames<T extends string> =
    T extends `${string}\${${infer Name}}${infer Rest}`
        ? (Name extends `${infer Head}.${string}` ? Head : Name) | TemplateParamNames<Rest>
        : never;

// the data object interpolate() expects for a given template literal; a
// non-literal string degrades to an open record, a union of templates
// distributes (`T extends string` is the distributive form), and a template
// without placeholders only accepts an empty object
export type TemplateData<T extends string> = T extends string
    ? string extends T
        ? Record<string, unknown>
        : [TemplateParamNames<T>] extends [never]
            ? Record<string, never>
            : { [K in TemplateParamNames<T>]: unknown }
    : never;

/**
 * interpolate
 * desc: utility function for string interpolation
 * note: useful for JSON since ES6 template syntax isn't supported
 * @param  {string} text  raw text
 * @param  {object} data  data object (optional)
 * @return {string}       interpolated text || regEx match
 */
export function interpolate<T extends string>(text: T, data?: TemplateData<T>): string {
    // regEx matches ES6 template syntax and submatches inner text
    // e.g. match = ${name}; submatch = name;
    const regEx = /\${([A-Za-z0-9.]+)}/g;

    return text.replace(regEx, (match, submatch: string) => {
        let newText: unknown;

        // if data and props exist, then try to access values
        // note: submatch.split checks for nested objects (e.g. address.city)
        const props = submatch.split('.');
        if (data && props.length) {
            newText = _getObjProp(data as Record<string, unknown>, props);
        }

        // normalize data type as string
        // note: this allows falsey values to be rendered into dom
        if (newText === 0 || newText === false) {
            newText = String(newText);
        }

        // String.replace coerces the callback result, so non-string values pass through
        return (newText || match) as string;
    });
}


/**
 * encodePathValue
 * desc: encode a value for safe use as URL path segment(s)
 * note: percent-encodes the value so reserved characters and trailing/leading
 *       whitespace survive transmission, while preserving "/" as a literal path
 *       separator. The backend addresses items via a "/**" wildcard, so a "/"
 *       in the value must stay a separator (encoding it as %2F would break that
 *       and is rejected by the server). Without encoding, the browser strips a
 *       trailing space and misreads reserved characters, so the resulting path
 *       no longer matches the stored identifier.
 * @param  {string} value  raw path value
 * @return {string}        encoded path value (non-strings are returned as-is)
 */
export function encodePathValue(value: string): string {
    if (typeof value !== 'string') {
        return value;
    }

    return value.split('/').map(encodeURIComponent).join('/');
}


// ':param' / ':param?' segments of a route path — a colon opening a
// '/'-delimited segment, the same shape interpolateAppUrl's regex matches
type RouteSegments<T extends string> = T extends `${infer Head}/${infer Rest}` ? Head | RouteSegments<Rest> : T;
type OptionalRouteParam<S extends string> = S extends `:${infer Name}?` ? Name : never;
type RequiredRouteParam<S extends string> = S extends `:${string}?` ? never : S extends `:${infer Name}` ? Name : never;

// collapses the required/optional intersection into one object type (keeps
// hovers readable and `?` modifiers intact)
type Simplify<T> = { [K in keyof T]: T[K] };

// a route without params only accepts an empty object
type RouteParamsObject<Req extends string, Opt extends string> = [Req | Opt] extends [never]
    ? Record<string, never>
    : Simplify<{ [K in Req]: string | number } & { [K in Opt]?: string | number | null }>;

// the data object interpolateAppUrl() expects for a given route literal; a
// non-literal string degrades to an open record, and a union of routes
// distributes (`T extends string` is the distributive form) so a caller may
// satisfy whichever branch it takes
export type RouteParams<T extends string> = T extends string
    ? string extends T
        ? Record<string, string | number | null | undefined>
        : RouteParamsObject<RequiredRouteParam<RouteSegments<T>>, OptionalRouteParam<RouteSegments<T>>>
    : never;

/**
 * interpolateAppUrl
 * desc: utility function for url interpolation
 * note: useful for replacing route params e.g '/example/:id'
 * @param  {string} text  raw text
 * @param  {object} data  data object (optional)
 * @return {string}       interpolated text || regEx match
 */
export function interpolateAppUrl<T extends string>(text: T, data: RouteParams<T>): string {
    const values = data as Record<string, string | number | null | undefined>;
    const regEx = /(^|\/):(\w+\??)(?=\/|$)/g;
    return text.replace(regEx, (match, submatch1: string, submatch2: string) => {
        // check for optional param
        // note: app router uses "?" to declare an optional param
        const isOptionalParam = submatch2.endsWith('?');
        if (isOptionalParam) {
            // if optional param, remove question mark character
            submatch2 = submatch2.replace(/\?$/, '');
            // if no data exists, remove the optional param from url
            if (!values[submatch2]) return '';
        }

        return submatch1 + (values[submatch2] || match);
    });
}


/**
 * showErrorMessage
 * desc: common function to display error message
 * note: used to handle errors from API
 * @param  {object} err   error instance
 * @return {undefined}
 */
export function showErrorMessage(err: ShownError | null | undefined, { extraBody }: { extraBody?: string } = {}): void {
    if (err && err.name !== 'AbortError') {
        const errHeader = translate('toaster.apiError.header');
        // for error codes without a translation, surface the API's own description
        // and code instead of the generic default message
        const fallback = err.internalDescription ? `${err.internalDescription} (${err.errorCode})` : err.errorCode;
        let errBody = getErrorMessage(err.errorCode, err.context, fallback);
        const errorFooter = ({ id }: { id: string }) => {
            return <div><br/>{ id }</div>;
        };
        if (extraBody) {
            errBody += extraBody;
        }

        toastr.error(errHeader, errBody, {
            // @ts-expect-error toastr's types don't admit null here, but the
            // runtime treats null as "no footer component"
            component: err.id ? errorFooter({ id: err.id }) : null
        });
    }
}


/**
 * showUnsavedChangesConfirmation
 * desc: common function to display a confirmation toastr if user has unsaved changes
 * note: reused all over the application
 *
 * @param {object}
 * @param {function} onOk     - ok callback.
 * @param {function} onCancel - cancel callback.
 *
 * @return {undefined}
 */
export function showUnsavedChangesConfirmation({ onOk, onCancel }: { onOk?: () => void; onCancel?: () => void }): void {
    const toastrConfirmOptions = {
        onOk: () => {
            if (onOk) onOk();
        },
        onCancel: () => {
            if (onCancel) onCancel();
        },
        okText: translate('journeysDetail.unsavedChangesModal.ok'),
        cancelText: translate('journeysDetail.unsavedChangesModal.cancel')
    };

    toastr.confirm(translate('journeysDetail.unsavedChangesModal.text'), toastrConfirmOptions);
}


/**
 * shuffleArray
 * desc: randomize order of array
 * note: useful for unit tests and other randomization
 * @param  {array}  list  Original list
 * @return {array}        Randomized list
 */
export function shuffleArray<T>(arr: T[]): T[] {
    const list = [ ...arr ];
    let currIndex = list.length;

    // loop over list
    while (currIndex !== 0) {
        // assign randomIndex and decrement currIndex
        const randomIndex = Math.floor(_getRandomFraction() * currIndex);
        currIndex--;

        // swap randomIndex value w currIndex value
        [ list[currIndex], list[randomIndex] ] = [ list[randomIndex], list[currIndex] ];
    }

    return list;
}



const createComparableValueGetter = (prop: string) => (item: unknown): string => {
    if (prop) {
        // the property may hold anything with a toString(); nullish falls through to ''
        return ((item as Record<string, unknown> | null | undefined)?.[prop] as { toString(): string } | null | undefined)?.toString() ?? '';
    }

    if (typeof item === 'string') return item;
    if (typeof item === 'number' || typeof item === 'boolean') return item.toString();
    return '';
};

/**
 * sortAz
 * desc: sort list A-Z order by specified property
 * note: localeCompare takes an optional language code argument
 * @param  {array}  list  Original list
 * @param  {string} prop  Comparison property
 * @return {array}        Sorted list
 */
export function sortAz<T>(list: T[] = [], prop = '', opts: { locale?: string; toSorted?: boolean } = {}): T[] {
    let result = list;
    if (list.length) {
        const userLocale = opts.locale || constants.LOCALES.DEFAULT;
        const langCode = userLocale.split('-')[0];
        const getComparableValue = createComparableValueGetter(prop);

        const sortFu = (a: T, b: T) => getComparableValue(a).localeCompare(getComparableValue(b), langCode, { sensitivity: 'base' });

        if (opts['toSorted']) {
            result = list.toSorted(sortFu);
        } else {
            // in-place by design; result already references list
            list.sort(sortFu);
        }
    }

    return result;
}


/**
 * sortZa
 * desc: sort list Z-A order by specified property
 * note: localeCompare takes an optional language code argument
 * @param  {array}  list  Original list
 * @param  {string} prop  Comparison property
 * @return {array}        Sorted list
 */
export function sortZa<T>(list: T[] = [], prop = '', opts: { locale?: string; toSorted?: boolean } = {}): T[] {
    let result = list;
    if (list.length) {
        const userLocale = opts.locale || constants.LOCALES.DEFAULT;
        const langCode = userLocale.split('-')[0];
        const getComparableValue = createComparableValueGetter(prop);

        const sortFu = (a: T, b: T) => getComparableValue(b).localeCompare(getComparableValue(a), langCode, { sensitivity: 'base' });

        if (opts['toSorted']) {
            result = list.toSorted(sortFu);
        } else {
            // in-place by design; result already references list
            list.sort(sortFu);
        }
    }

    return result;
}


/**
 * wrapWithRouter
 * desc: helper function that wraps component with Router component
 * note: used in unit tests - uses MemoryRouter with real routing when route config provided
 * @param  {array|object}  Component     react component
 * @param  {object}        props         component props (optional)
 * @param  {object}        routeConfig   route configuration (optional)
 * @param  {string}        routeConfig.initialEntry  initial URL path (e.g., '/test-queue-id')
 * @param  {string}        routeConfig.routePath     route pattern (e.g., '/:id' or '/:id/*')
 */
export function wrapWithRouter(Component: ComponentType<Record<string, unknown>>, props?: Record<string, unknown>, routeConfig?: { initialEntry?: string; routePath?: string }) {
    if (routeConfig) {
        const { initialEntry = '/', routePath = '/*' } = routeConfig;

        return (
            <MemoryRouter initialEntries={ [ initialEntry ] }>
                <Routes>
                    <Route path={ routePath } element={ <Component navigate={ globalThis.mockNavigate } location={ globalThis.mockLocation } { ...props }/> } />
                </Routes>
            </MemoryRouter>
        );
    }
    return <BrowserRouter><Component navigate={ globalThis.mockNavigate } location={ globalThis.mockLocation } { ...props }/></BrowserRouter>;
}



//*************************************************
//*****   Helper Functions   **********************
//*************************************************
/**
 * _getRandomFraction
 * desc: uniform random number in [0, 1), like Math.random
 * note: backed by the Web Crypto API — none of the callers are
 *       security-sensitive, but it satisfies Sonar S2245 at no cost
 */
function _getRandomFraction(): number {
    const [ value ] = crypto.getRandomValues(new Uint32Array(1));
    return value / 2 ** 32;
}

/**
 * Get Object Property
 * desc: recursive function to access nested objects
 * note: requirement of interpolation utility function
 * @param  {object} obj    Data object
 * @param  {array}  props  Object prop(s)
 * @return {*}             Property value
 */
function _getObjProp(obj: Record<string, unknown>, props: string[]): unknown {
    // no props left resolves to undefined
    if (props.length) {
        const value = obj[props[0]];
        // if prop value is an object, dig deeper
        // (null also passes the typeof check and crashes in the recursive call)
        if (typeof value === 'object') {
            return _getObjProp(value as Record<string, unknown>, props.slice(1));
        }
        // otherwise, return prop value
        return value;
    }
}

/**
 * toLocalDateString
 * desc: converts a Date object to a local date string (yyyy-MM-dd)
 * @param {Date} date        Date object
 * @return {string}          Date string in yyyy-MM-dd format
 */
export function toLocalDateString(date: Date): string {
    if (!date || !(date instanceof Date)) return '';
    return `${date.getFullYear()}-${getTwoDigitString(date.getMonth() + 1)}-${getTwoDigitString(date.getDate())}`;
}

/**
 * fromLocalDateString
 * desc: converts a local date string (yyyy-MM-dd) to a Date object using local time
 * @param {string} dateString   Date string in yyyy-MM-dd format
 * @param {boolean} endOfDay    If true, sets time to 23:59:59
 * @return {Date|null}          Date object or null if invalid
 */
export function fromLocalDateString(dateString: string, endOfDay?: boolean): Date | null {
    if (!dateString || typeof dateString !== 'string') return null;
    const time = endOfDay ? 'T23:59:59' : 'T00:00:00';
    const date = new Date(dateString + time);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * omitDeprecatedFieldKeys
 * desc: strip deprecated attributes from an outgoing order-schema fields array before saving.
 *       isDependentColumn is still emitted by the API on read but must no longer be sent back —
 *       dependency is implicit now (a linked column that is not the collection's primary key).
 * @param {Array} fields     order schema metadata objects
 * @return {Array}           the same fields without deprecated keys
 */
export function omitDeprecatedFieldKeys<T extends object>(fields: T[] = []): T[] {
    return fields.map((field) => {
        const result = { ...field };
        delete (result as Record<string, unknown>).isDependentColumn;
        return result;
    });
}

export default { downloadFileClient };
